import { useCallback, useEffect, useState } from 'react';
import { useShadowRoot } from '@backstage/plugin-techdocs-react';
import type { ReaderToc, TocHeading } from './types';

/*
 * The reader's table of contents and its scroll-spy.
 *
 * mkdocs-material ships a table of contents but its scroll-spy is driven by
 * material's own JavaScript, which TechDocs does not run — the stock rail is
 * therefore inert, and `readerStyles` hides it. This hook reads the headings
 * straight out of the shadow root and tracks the active one on scroll, for
 * whichever surface is presenting them: the desktop rail or the mobile pill.
 */
const ACTIVE_OFFSET_PX = 140;

const readHeadings = (root: ShadowRoot): TocHeading[] =>
  Array.from(
    root.querySelectorAll<HTMLElement>(
      '.md-content__inner h2, .md-content__inner h3',
    ),
  )
    .filter(element => Boolean(element.id))
    .map(element => ({
      id: element.id,
      // The permalink anchor mkdocs appends is part of the heading's text.
      label: (element.textContent ?? '').replace(/[¶#]\s*$/, '').trim(),
      depth: element.tagName === 'H3' ? 2 : 1,
    }));

export const useReaderToc = (): ReaderToc => {
  const shadowRoot = useShadowRoot();
  const [headings, setHeadings] = useState<TocHeading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (!shadowRoot) return undefined;
    /*
     * The observer fires for every mutation in the reader, including the ones
     * the other addons make. Replacing the state with a fresh array each time
     * would re-render on every one of them and keep restarting the scroll-spy
     * below, so the list is only swapped when the headings actually change.
     */
    const sync = () => {
      const next = readHeadings(shadowRoot);
      setHeadings(current =>
        current.length === next.length &&
        current.every(
          (h, i) => h.id === next[i].id && h.label === next[i].label,
        )
          ? current
          : next,
      );
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(shadowRoot, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [shadowRoot]);

  useEffect(() => {
    if (!shadowRoot || headings.length === 0) return undefined;
    let frame = 0;

    const spy = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // The last heading whose top has passed the offset is the one being
        // read; before any has, the first entry stays active.
        let current = headings[0].id;
        for (const heading of headings) {
          const element = shadowRoot.getElementById(heading.id);
          if (!element) continue;
          if (element.getBoundingClientRect().top <= ACTIVE_OFFSET_PX) {
            current = heading.id;
          }
        }
        setActiveId(current);
      });
    };

    spy();
    // The reader's scroll container is not the window, so listen in the
    // capture phase to catch scrolls from whichever ancestor is scrolling.
    window.addEventListener('scroll', spy, true);
    window.addEventListener('resize', spy);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', spy, true);
      window.removeEventListener('resize', spy);
    };
  }, [shadowRoot, headings]);

  const jumpTo = useCallback(
    (id: string) => {
      shadowRoot
        ?.getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [shadowRoot],
  );

  const activeIndex = headings.findIndex(heading => heading.id === activeId);

  return {
    headings,
    activeId,
    // Before the spy's first pass nothing is active; the first heading is the
    // one on screen, so the pill counts from 1 rather than showing 0.
    activeIndex: activeIndex === -1 ? 0 : activeIndex,
    jumpTo,
  };
};
